/**
 * Helpers that operate on the ProseMirror JSON document stored in
 * `ai_prompts.instructions_doc`. Kept framework-agnostic so they can be
 * reused for table previews, snapshots, and future renderer plumbing
 * without dragging TipTap into the call site.
 */

import { docToPlainText as docToText } from "@/lib/prosemirror/plain-text";

import type { AiPromptInstructionsDoc } from "./types";

/**
 * Loose mirror of TipTap's `JSONContent` shape that doesn't require
 * importing the TipTap runtime. We only care about `type`, `text`,
 * `attrs`, and `content` for the previews.
 */
interface DocNode {
  type?: string;
  text?: string;
  attrs?: Record<string, unknown> | null;
  content?: DocNode[];
}

/**
 * Flatten a ProseMirror JSON document into a single-line plain-text
 * preview suitable for table cells, list rows, and tooltips.
 *
 * Wrapper over the shared `@/lib/prosemirror` walker that pins the
 * ai-prompts preview format — space-joined tokens with `[Label]` (or
 * `[key]` when the `label` attribute is missing) variable chips. It stays
 * separate from sequences' `{Label}` subject format on purpose (ARBI-851).
 */
export function docToPlainText(
  doc: AiPromptInstructionsDoc | DocNode | null | undefined,
): string {
  return docToText(doc, {
    singleLine: true,
    separator: " ",
    variableMention: (attrs) => {
      const label =
        typeof attrs.label === "string" && attrs.label.length > 0
          ? attrs.label
          : typeof attrs.key === "string"
            ? attrs.key
            : "";
      return `[${label}]`;
    },
  });
}
